<script>
import axios from '@/utils/axios';
import HrmPayslipModal from '@/components/modal/hrm-payslip-modal.vue';
import HrmPayrollHoldModal from '@/components/modal/hrm-payroll-hold-modal.vue';

const MONTHS = ['', 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export default {
  components: { HrmPayslipModal, HrmPayrollHoldModal },
  inject: ['setLoading'],
  data() {
    return {
      runId: this.$route.params.id,
      run: null,
      loadingRun: true,
      employees: [],
      loadingEmp: false,
      empStatusOptions: [
        { label: 'Pending', value: 'pending' },
        { label: 'Processed', value: 'processed' },
        { label: 'On Hold', value: 'on_hold' },
      ],
      empFilters: { status: '' },
      empColumns: [
        { key: 'employee', label: 'Employee' },
        { key: 'days', label: 'P / A / Leave' },
        { key: 'late', label: 'Late / Penalty Days' },
        { key: 'late_ded', label: 'Late Deduction' },
        { key: 'ctc', label: 'CTC (Monthly)' },
        { key: 'gross', label: 'Gross' },
        { key: 'deductions', label: 'Total Deductions' },
        { key: 'net', label: 'Net Salary' },
        { key: 'status', label: 'Status' },
        { key: 'actions', label: 'Actions', width: '110px' },
      ],
      pagination: { current_page: 1, per_page: 30, last_page: 1, total: 0, links: [] },
    };
  },
  computed: {
    period() {
      if (!this.run) return '';
      return `${MONTHS[this.run.month] || this.run.month} ${this.run.year}`;
    },
    canProcess() {
      return this.run && this.run.status !== 'finalized';
    },
    canFinalize() {
      return this.run && this.run.status === 'processed';
    },
  },
  mounted() {
    this.fetchRun();
    this.fetchEmployees();
  },
  methods: {
    monthName(m) { return MONTHS[m] || m; },
    money(v) {
      const n = parseFloat(v);
      if (isNaN(n)) return '-';
      return '₹' + n.toLocaleString('en-IN', { maximumFractionDigits: 0 });
    },
    num(v) { return parseFloat(v) || 0; },
    empName(row) {
      const e = row.employee || {};
      return e.full_name || [e.first_name, e.last_name].filter(Boolean).join(' ') || '-';
    },
    empCode(row) { return row.employee?.code || ''; },

    // Run fetch — runs list se nikaalte (koi single-run API nahi)
    async fetchRun() {
      this.loadingRun = true;
      try {
        const res = await axios.get('/hrms/payroll', { params: { per_page: 100 } });
        const list = res.data.data?.data || [];
        this.run = list.find(r => String(r.id) === String(this.runId)) || null;
        if (!this.run) this.$swalToast('Payroll run not found.', 'error');
      } catch (e) { this.$swalToast(e.message, 'error'); }
      finally { this.loadingRun = false; }
    },

    async fetchEmployees(page = 1) {
      this.loadingEmp = true;
      try {
        const res = await axios.get(`/hrms/payroll/${this.runId}/employees`, {
          params: { page, status: this.empFilters.status || undefined },
        });
        const d = res.data.data;
        this.employees = d.data;
        this.pagination = { current_page: d.current_page, per_page: d.per_page, last_page: d.last_page, total: d.total, links: d.links };
      } catch (e) { this.$swalToast(e.message, 'error'); }
      finally { this.loadingEmp = false; }
    },

    async processRun() {
      this.setLoading(true);
      try {
        const res = await axios.post(`/hrms/payroll/${this.runId}/process`);
        const d = res.data.data || {};
        let msg = res.data.message || 'Payroll processed.';
        if (d.processed !== undefined) {
          msg = `Processed: ${d.processed}, Skipped: ${d.skipped || 0}`;
        }
        this.$swalToast(msg);
        this.fetchRun();
        this.fetchEmployees(1);
      } catch (e) { this.$swalToast(e.message, 'error'); }
      finally { this.setLoading(false); }
    },

    async finalizeRun() {
      this.setLoading(true);
      try {
        const res = await axios.post(`/hrms/payroll/${this.runId}/finalize`);
        this.$swalToast(res.data.message || 'Payroll finalized.');
        this.fetchRun();
      } catch (e) { this.$swalToast(e.message, 'error'); }
      finally { this.setLoading(false); }
    },

    confirmProcess() { this.$openModal('hrm_payroll_process_modal'); },
    confirmFinalize() { this.$openModal('hrm_payroll_finalize_modal'); },
    doProcess() { this.$closeModal('hrm_payroll_process_modal'); this.processRun(); },
    doFinalize() { this.$closeModal('hrm_payroll_finalize_modal'); this.finalizeRun(); },

    goBack() { this.$router.push({ name: 'hrms-payroll-runs' }); },

    openPayslip(row) {
      this.$refs.payslipModal.open(row.employee_id, this.run.month, this.run.year);
    },
    openHold(row) {
      this.$refs.holdModal.open(this.runId, row.employee_id, this.empName(row));
    },
  },
};
</script>

<template>
  <layout-header></layout-header>
  <layout-sidebar></layout-sidebar>

  <div class="page-wrapper">
    <div class="content">

      <!-- Back + Title -->
      <div class="d-flex align-items-center gap-2 mb-3">
        <button class="pr-back" @click="goBack"><i class="ti ti-arrow-left"></i></button>
        <h4 class="page-title mb-0">Payroll Run</h4>
      </div>

      <!-- Run Header Card -->
      <div v-if="loadingRun" class="pr-state"><div class="spinner-border text-primary"></div></div>
      <div v-else-if="run" class="pr-run-card mb-3">
        <div class="pr-run-left">
          <div class="pr-run-ico"><i class="ti ti-calendar-dollar"></i></div>
          <div>
            <div class="pr-run-period">{{ period }}</div>
            <div class="pr-run-sub">
              <span class="pr-st" :class="'pr-' + run.status">{{ run.status }}</span>
              <span class="pr-run-days">{{ run.working_days }} working days</span>
            </div>
          </div>
        </div>
        <div class="pr-run-actions">
          <button v-if="canProcess" class="btn btn-outline-primary" @click="confirmProcess">
            <i class="ti ti-player-play me-1"></i>{{ run.status === 'draft' ? 'Process' : 'Re-process' }}
          </button>
          <button v-if="canFinalize" class="btn btn-primary" @click="confirmFinalize">
            <i class="ti ti-lock me-1"></i>Finalize
          </button>
          <span v-if="run.status === 'finalized'" class="pr-locked"><i class="ti ti-lock-check me-1"></i>Finalized & Locked</span>
        </div>
      </div>

      <!-- Employees Table -->
      <div class="card border-0 shadow-sm mb-0" style="border-radius:14px;">
        <div class="card-header bg-white border-bottom d-flex align-items-center justify-content-between flex-wrap gap-2 py-3 px-4">
          <h5 class="mb-0 fw-bold" style="font-size:14px; color:#0f172a;">
            Employees <span style="font-size:11px; color:#94a3b8; font-weight:500; margin-left:6px;">({{ pagination.total }})</span>
          </h5>
          <vue-select class="pr-vsel" v-model="empFilters.status" :options="empStatusOptions" label="label" :reduce="s => s.value"
            placeholder="All Status" :clearable="true" @update:modelValue="fetchEmployees(1)" style="min-width:160px;" />
        </div>

        <div class="card-body px-3 pt-2 pb-3" style="background:#f1f5f9;">
          <app-table
            :columns="empColumns" :rows="employees" :pagination="pagination" :loading="loadingEmp"
            empty-text="No employees processed yet. Click Process to calculate salaries."
            empty-icon="ti ti-users"
            @page-change="fetchEmployees"
          >
            <template #employee="{ row }">
              <div class="d-flex flex-column">
                <span style="font-size:12.5px; font-weight:600; color:#1e293b;">{{ empName(row) }}</span>
                <span v-if="empCode(row)" style="font-size:10px; color:#94a3b8; font-weight:700;">{{ empCode(row) }}</span>
              </div>
            </template>
            <template #days="{ row }">
              <span style="font-size:11.5px; color:#475569;">
                <span style="color:#16a34a;">{{ num(row.present_days) }}</span> /
                <span style="color:#dc2626;">{{ num(row.absent_days) }}</span> /
                <span style="color:#ea580c;">{{ num(row.paid_leave_days) + num(row.unpaid_leave_days) }}</span>
              </span>
            </template>
            <template #late="{ row }">
              <span style="font-size:11.5px; color:#475569;">
                <span style="color:#ea580c; font-weight:600;">{{ row.late_count }}</span> late
                <span v-if="num(row.late_penalty_days) > 0" style="color:#dc2626;">
                  / {{ num(row.late_penalty_days) }} day penalty
                </span>
                <span v-else style="color:#94a3b8;">/ no penalty</span>
              </span>
            </template>

            <template #late_ded="{ row }">
              <span :style="{ color: num(row.late_deduction) > 0 ? '#dc2626' : '#94a3b8' }">
                {{ money(row.late_deduction) }}
              </span>
            </template>
            <template #ctc="{ row }">{{ money(row.ctc_monthly) }}</template>
            <template #gross="{ row }">{{ money(row.gross_salary) }}</template>
            <template #deductions="{ row }">
              <span style="color:#dc2626;">
                {{ money(num(row.total_deductions) + num(row.unpaid_deduction) + num(row.late_deduction)) }}
              </span>
            </template>
            <template #net="{ row }">
              <span class="fw-bold" style="color:#0f172a;">{{ money(row.net_salary) }}</span>
            </template>
            <template #status="{ row }">
              <span class="pr-est" :class="'pr-e-' + row.status">{{ (row.status || '').replace('_', ' ') }}</span>
            </template>
            <template #actions="{ row }">
              <div class="d-flex gap-1 justify-content-end">
                <button class="btn btn-sm btn-light" style="color:#4f46e5;" title="View Payslip" @click="openPayslip(row)">
                  <i class="ti ti-file-invoice"></i>
                </button>
                <button v-if="run && run.status !== 'finalized' && row.status !== 'on_hold'" class="btn btn-sm btn-light text-danger" title="Hold" @click="openHold(row)">
                  <i class="ti ti-hand-stop"></i>
                </button>
              </div>
            </template>
          </app-table>
        </div>
      </div>

    </div>
    <app-footer></app-footer>
  </div>

  <!-- Process confirm -->
  <div class="modal fade" id="hrm_payroll_process_modal">
    <div class="modal-dialog modal-dialog-centered"><div class="modal-content"><div class="modal-body text-center">
      <span class="avatar avatar-xl bg-transparent-primary text-primary mb-3"><i class="ti ti-player-play fs-36"></i></span>
      <h4 class="mb-1">Process Payroll</h4>
      <p class="mb-3">This will calculate salaries for all active employees based on attendance. You can re-process until finalized.</p>
      <div class="d-flex justify-content-center">
        <a href="javascript:void(0);" class="btn btn-light me-3" data-bs-dismiss="modal">Cancel</a>
        <a href="javascript:void(0);" class="btn btn-primary" @click="doProcess">Yes, Process</a>
      </div>
    </div></div></div>
  </div>

  <!-- Finalize confirm -->
  <div class="modal fade" id="hrm_payroll_finalize_modal">
    <div class="modal-dialog modal-dialog-centered"><div class="modal-content"><div class="modal-body text-center">
      <span class="avatar avatar-xl bg-transparent-success text-success mb-3"><i class="ti ti-lock fs-36"></i></span>
      <h4 class="mb-1">Finalize Payroll</h4>
      <p class="mb-3">Once finalized, this payroll is locked and cannot be re-processed. Continue?</p>
      <div class="d-flex justify-content-center">
        <a href="javascript:void(0);" class="btn btn-light me-3" data-bs-dismiss="modal">Cancel</a>
        <a href="javascript:void(0);" class="btn btn-success" @click="doFinalize">Yes, Finalize</a>
      </div>
    </div></div></div>
  </div>

  <hrm-payslip-modal ref="payslipModal" />
  <hrm-payroll-hold-modal ref="holdModal" @held="fetchEmployees(pagination.current_page)" />
</template>

<style scoped>
.pr-back { width:36px; height:36px; border-radius:10px; border:1px solid #e2e8f0; background:#fff; color:#475569; display:inline-flex; align-items:center; justify-content:center; font-size:18px; cursor:pointer; transition:all .15s; }
.pr-back:hover { border-color:#cbd5e1; color:#1e293b; }
.pr-state { text-align:center; padding:40px; }

.pr-run-card { display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:14px; background:#fff; border:1px solid #eef2f7; border-radius:16px; padding:20px 22px; box-shadow:0 1px 3px rgba(0,0,0,.03); }
.pr-run-left { display:flex; align-items:center; gap:16px; }
.pr-run-ico { width:54px; height:54px; border-radius:14px; background:linear-gradient(135deg,#4f46e5,#7c3aed); color:#fff; display:flex; align-items:center; justify-content:center; font-size:26px; box-shadow:0 8px 18px -6px rgba(79,70,229,.5); }
.pr-run-period { font-size:20px; font-weight:800; color:#0f172a; line-height:1.1; }
.pr-run-sub { display:flex; align-items:center; gap:10px; margin-top:6px; }
.pr-run-days { font-size:12px; color:#94a3b8; }
.pr-run-actions { display:flex; align-items:center; gap:10px; }
.pr-run-actions .btn { border-radius:10px; font-size:13px; font-weight:600; padding:9px 18px; }
.btn-primary { background:linear-gradient(135deg,#4f46e5,#7c3aed); border:none; }
.pr-locked { font-size:13px; font-weight:600; color:#16a34a; display:inline-flex; align-items:center; }

.pr-st { display:inline-block; font-size:10px; font-weight:600; padding:3px 10px; border-radius:20px; text-transform:capitalize; }
.pr-draft { background:#f1f5f9; color:#64748b; }
.pr-processed { background:#dbeafe; color:#2563eb; }
.pr-finalized { background:#dcfce7; color:#16a34a; }

.pr-est { display:inline-block; font-size:10px; font-weight:600; padding:3px 10px; border-radius:20px; text-transform:capitalize; }
.pr-e-pending { background:#fef3c7; color:#b45309; }
.pr-e-processed { background:#dbeafe; color:#2563eb; }
.pr-e-on_hold { background:#fee2e2; color:#dc2626; }

:deep(.pr-vsel .vs__dropdown-toggle) { border:1px solid #e2e8f0; border-radius:10px; min-height:34px; background:#fff; padding:0 4px; }
:deep(.pr-vsel .vs__dropdown-toggle:focus-within) { border-color:#a5b4fc; box-shadow:0 0 0 3px rgba(79,70,229,.1); }
:deep(.pr-vsel .vs__search) { font-size:12.5px; margin:0; padding:2px 4px; }
:deep(.pr-vsel .vs__selected) { font-size:12.5px; color:#1e293b; margin:2px; }
:deep(.pr-vsel .vs__dropdown-option) { font-size:12.5px; }
</style>